import { useState } from 'react';
import { Form, Input, InputNumber, Select, Upload, Checkbox } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { toast } from 'react-hot-toast';
import { ALL_OPTION_TYPES, ALL_INPUT_TYPES, OPTION_UNIT_OPTIONS } from '../../../constants';

export const OptionForm: React.FC = () => {
  const form = Form.useFormInstance();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleImageChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    form.setFieldValue('optionImage', newFileList);
  };

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      toast.error('You can only upload image files!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      toast.error('Image must be smaller than 5MB!');
      return false;
    }
    return false;
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <div className="pt-4">
      <Form.Item
        label="Option Name"
        name="dogma_name"
        rules={[{ required: true, message: 'Please enter option name' }]}
      >
        <Input placeholder="Enter option name" />
      </Form.Item>

      <Form.Item
        label="Description"
        name="dogma_description"
      >
        <Input.TextArea placeholder="Enter description" rows={2} />
      </Form.Item>

      <Form.Item
        label="Option Type"
        name="dogma_optiontype"
        rules={[{ required: true, message: 'Please select option type' }]}
      >
        <Select
          placeholder="Search and select option type"
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
          }
        >
          {ALL_OPTION_TYPES.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Input Type"
        name="dogma_inputtype"
        rules={[{ required: true, message: 'Please select input type' }]}
      >
        <Select
          placeholder="Search and select input type"
          showSearch
          filterOption={(input, option) =>
            (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
          }
        >
          {ALL_INPUT_TYPES.map((inputType) => (
            <Select.Option key={inputType.value} value={inputType.value}>
              {inputType.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Default Price (£)"
        name="dogma_pricemodifier"
        tooltip="The price used when no conditional price rule matches"
        rules={[{ required: true, message: 'Please enter price modifier' }]}
      >
        <InputNumber
          placeholder="0.00"
          precision={2}
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item
        label="Option Unit"
        name="dogma_optionunit"
      >
        <Select placeholder="Select unit">
          {OPTION_UNIT_OPTIONS.map((u) => (
            <Select.Option key={u.value} value={u.value}>
              {u.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Value"
        name="dogma_value"
      >
        <Input placeholder="Enter value (optional)" />
      </Form.Item>

      <Form.Item
        name="dogma_repeatperwindowframe"
        valuePropName="checked"
        tooltip="When enabled, this option will be applied to each window frame individually rather than once for the entire order"
      >
        <Checkbox>Repeat per Window Frame</Checkbox>
      </Form.Item>

      <Form.Item
        label="Option Image"
        name="optionImage"
        valuePropName="fileList"
        getValueFromEvent={(e) => {
          if (Array.isArray(e)) return e;
          return e?.fileList;
        }}
        style={{ marginBottom: '24px' }}
      >
        <Upload
          listType="picture-card"
          fileList={fileList}
          onChange={handleImageChange}
          beforeUpload={beforeUpload}
          maxCount={1}
          accept="image/*"
          style={{ display: 'block' }}
        >
          {fileList.length >= 1 ? null : uploadButton}
        </Upload>
      </Form.Item>
    </div>
  );
};
